import { CustomField } from 'src/app/+shared/custom-fields/models';
import { CustomFieldConfig } from 'src/app/+shared/custom-fields/models/customFieldConfig';
import { InstallationFlowType } from 'src/app/constants/app.constants';
import {
  personalDataCustomFields,
  districtHeatDeviceCustomFields,
  gasDeviceCustomFields,
  waterDeviceCustomFields,
} from 'src/app/+shared/custom-fields/common-custom-fields';
import { GasDeviceObject } from 'src/app/jurisdiction/facilities-form/facilities-form.domain';
import { CommonService } from 'src/app/services/common.service';

const personalDataFields: CustomField[] = [
  { ...personalDataCustomFields.addressSuffix, row: 1 },
  { ...personalDataCustomFields.housingUnits, row: 2 },
  { ...personalDataCustomFields.apartmentNo, row: 2 },
  { ...personalDataCustomFields.floor, row: 3 },
];

const districtHeatDeviceFields: CustomField[] = [
  { ...districtHeatDeviceCustomFields.districtHeatType, row: 1 },
  { ...districtHeatDeviceCustomFields.districtTypeOfHeating, row: 1 },
  { ...districtHeatDeviceCustomFields.heatingLoad, row: 2 },
  { ...districtHeatDeviceCustomFields.installHeatPower, row: 2 },
  { ...districtHeatDeviceCustomFields.floorHeating, row: 3 },
  { ...districtHeatDeviceCustomFields.dhw, row: 4 },
  { ...districtHeatDeviceCustomFields.designTempTv, row: 4 },
  { ...districtHeatDeviceCustomFields.designTempTr, row: 5 },
  { ...districtHeatDeviceCustomFields.designTempFloorHeatingTv, row: 6 },
  { ...districtHeatDeviceCustomFields.designTempFloorHeatingTr, row: 7 },
  { ...districtHeatDeviceCustomFields.thermoValveType, row: 8 },
  { ...districtHeatDeviceCustomFields.flowRadiators, row: 8 },
  { ...districtHeatDeviceCustomFields.deviation, row: 9 },
  { ...districtHeatDeviceCustomFields.flowFloorHeating, row: 9 },
];

const waterDeviceFields: CustomField[] = [
  { ...waterDeviceCustomFields.specialApplianceDropDown, row: 1, required: true },
  { ...waterDeviceCustomFields.amount, row: 1, required: true },
  { ...waterDeviceCustomFields.vrCold, row: 2 },
  { ...waterDeviceCustomFields.vrHot, row: 2 },
];

const technicalDataFields: CustomField[] = [...districtHeatDeviceFields];

// LSW Gas devices before 20 January 2023 had different data format and we want the user to be able to see them correctly
const isOldLswGasDeviceApplication = (_: GasDeviceObject, isReadOnly: boolean, commonService: CommonService) => {
  const applicationGasDeviceDataTypeChangeDate = '2023-01-20';

  return (
    isReadOnly &&
    commonService.applicationSubmissionDate &&
    commonService.applicationSubmissionDate < applicationGasDeviceDataTypeChangeDate
  );
};

const gasDeviceFields: CustomField[] = [
  { ...gasDeviceCustomFields.ceNumber, row: 1, displayFunction: (value: GasDeviceObject) => !value.deviceRemovedCheckbox },
  {
    ...gasDeviceCustomFields.deviceName,
    row: 2,
    displayFunction: isOldLswGasDeviceApplication,
  },
  { ...gasDeviceCustomFields.power, row: 2 },
  {
    ...gasDeviceCustomFields.selectedGasCategory,
    row: 10,
    readonly: true,
    defaultValue: 'h_gas',
    displayFunction: (value: GasDeviceObject) => !value.deviceRemovedCheckbox,
  },
];

const gasApplianceFields = [
  {
    ...gasDeviceCustomFields.applianceType,
    row: 5,
    displayFunction: (value: GasDeviceObject, isReadOnly: boolean, commonService: CommonService) =>
      !isOldLswGasDeviceApplication(value, isReadOnly, commonService),
  },
  {
    ...gasDeviceCustomFields.manufacturer,
    row: 6,
    displayFunction: (value: GasDeviceObject, isReadOnly: boolean, commonService: CommonService) =>
      !isOldLswGasDeviceApplication(value, isReadOnly, commonService),
  },
  { ...gasDeviceCustomFields.otherManufacturer, row: 6 },
  {
    ...gasDeviceCustomFields.deviceType,
    row: 7,
    displayFunction: (value: GasDeviceObject, isReadOnly: boolean, commonService: CommonService) =>
      !isOldLswGasDeviceApplication(value, isReadOnly, commonService),
  },
  {
    ...gasDeviceCustomFields.manufactureYear,
    row: 8,
    displayFunction: (value: GasDeviceObject, isReadOnly: boolean, commonService: CommonService) =>
      !value.deviceRemovedCheckbox && !isOldLswGasDeviceApplication(value, isReadOnly, commonService),
  },
  {
    ...gasDeviceCustomFields.applianceCategory,
    row: 9,
    displayFunction: (value: GasDeviceObject, isReadOnly: boolean, commonService: CommonService) =>
      !value.deviceRemovedCheckbox && !isOldLswGasDeviceApplication(value, isReadOnly, commonService),
  },
];

const applicationsWithGasAppliance = [
  InstallationFlowType.NewInstallation,
  InstallationFlowType.UpdateInstallation,
  InstallationFlowType.ConversionToGas,
];

export const configs: CustomFieldConfig[] = Object.values(InstallationFlowType).map(applicationType => {
  const applicationSpecificFields: CustomField[] = [];

  if (applicationsWithGasAppliance.includes(applicationType)) {
    applicationSpecificFields.push(...gasApplianceFields);
  }

  if (applicationType !== InstallationFlowType.NewInstallation) {
    applicationSpecificFields.push({
      ...gasDeviceCustomFields.deviceRemovedCheckbox,
      row: 1,
    });
  }

  return {
    applicationType,
    fields: [...personalDataFields, ...technicalDataFields, ...gasDeviceFields, ...waterDeviceFields, ...applicationSpecificFields],
  };
});
